import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button, ButtonThemeEnum } from "../../Buttons/styles";
import { LoadMoreRow, LoadMoreSentinel } from "../../History/style";
import { Spinner } from "../Spinner";

/**
 * Walks up the DOM to the nearest ancestor with a vertical scroll surface.
 * The IntersectionObserver uses it as its clipping root so auto-load works
 * inside the trading bottom-bar drawer (which owns the scroll), falling back
 * to the viewport on Portfolio routes. Mirrors HistoryTableShell.
 */
function findScrollParent(node: HTMLElement | null): Element | null {
  let el: HTMLElement | null = node?.parentElement ?? null;
  while (el) {
    const { overflowY } = window.getComputedStyle(el);
    if (/(auto|scroll|overlay)/.test(overflowY)) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

interface ITableLoadMoreProps {
  /** Column count for the spanning `<td>`. */
  colSpan: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  /**
   * Number of rows currently rendered. Re-arms the observer as the list grows
   * so the sentinel keeps firing after each appended page.
   */
  itemCount: number;
}

/**
 * Drop-in `<tr>` sentinel that auto-loads the next cursor page when scrolled
 * into view, with an explicit "Load more" button fallback. Shared by the
 * trading bottom-bar Open Orders / Positions tables to match the History
 * tabs' infinite-scroll UX. Render only when `hasMore` is true.
 */
function TableLoadMore({
  colSpan,
  hasMore,
  isLoadingMore,
  onLoadMore,
  itemCount,
}: ITableLoadMoreProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "components.History.HistoryTableShell",
  });
  const sentinelRef = useRef<HTMLTableRowElement>(null);
  // Guards against the observer firing `onLoadMore` twice within a single load
  // cycle: with a 200px rootMargin the sentinel stays intersecting, and
  // `isLoadingMore` only flips a render later. The ref blocks re-entry until
  // the effect re-runs (new page → itemCount change, or isLoadingMore toggle).
  const firedRef = useRef(false);

  useEffect(() => {
    if (!hasMore || !sentinelRef.current) return undefined;
    firedRef.current = false;
    const node = sentinelRef.current;
    const root = findScrollParent(node);
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isLoadingMore && !firedRef.current) {
          firedRef.current = true;
          onLoadMore();
        }
      },
      { root, rootMargin: "200px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, onLoadMore, itemCount]);

  if (!hasMore) return null;

  return (
    <LoadMoreRow ref={sentinelRef}>
      <td colSpan={colSpan}>
        <LoadMoreSentinel>
          {isLoadingMore ? (
            <Spinner />
          ) : (
            <Button buttonTheme={ButtonThemeEnum.NEUTRAL2} onClick={onLoadMore}>
              {t("load_more", { defaultValue: "Load more" })}
            </Button>
          )}
        </LoadMoreSentinel>
      </td>
    </LoadMoreRow>
  );
}

export default TableLoadMore;
